import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { EnTetePage } from "../components/EnTetePage";
import { CarteFormation } from "../components/CarteFormation";
import { CallToAction } from "../components/CallToAction";
import { PageIntrouvable } from "./PageIntrouvable";
import { services, serviceParSlug, formationParSlug } from "../data/mockData";

/** Type de projet présélectionné dans le formulaire de contact. */
const sujetContact = {
  "traitement-donnees-geospatiales": "traitement",
  "automatisation-sig": "automatisation",
  "diffusion-visualisation": "diffusion",
  "formation-accompagnement": "formation",
  "cartes-web-developpement-sur-mesure": "developpement",
  "sous-traitance-traitement-collecte": "sous-traitance",
};

export const ServicePage = () => {
  const { slug } = useParams();
  const service = serviceParSlug(slug);
  if (!service) return <PageIntrouvable />;

  const formationsLiees = service.formations.map(formationParSlug).filter(Boolean);
  const autresServices = services.filter((s) => s.slug !== slug);

  return (
    <>
      <EnTetePage
        chemin={[
          ["Accueil", "/"],
          ["Services", "/services"],
        ]}
        titre={service.title}
        chapo={service.description}
      >
        <Link to={`/?sujet=${sujetContact[slug]}#contact`} className="btn-accent">
          Décrire votre besoin
          <ArrowRight className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
        </Link>
        <Link to="/formations" className="btn-outline">
          Voir les formations
        </Link>
      </EnTetePage>

      <section className="section">
        <div className="shell grid gap-8 lg:grid-cols-5 lg:gap-12">
          <div className="lg:col-span-2">
            <h2 className="h2">Ce que nous faisons</h2>
            <p className="mt-6 text-vt-chapo leading-relaxed text-t2">{service.intro}</p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-3">
            {service.details.map((detail) => (
              <div key={detail.titre} className="card">
                <Check className="card-ico" strokeWidth={2.2} aria-hidden="true" />
                <h3 className="text-vt-h3 font-bold text-brand">{detail.titre}</h3>
                <p className="mt-2 leading-relaxed text-t2">{detail.texte}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="shell grid gap-6 lg:grid-cols-2">
          <div className="rounded-[20px] border border-border bg-white p-8 lg:p-10">
            <h2 className="text-vt-h3 font-extrabold text-brand">Ce que vous recevez</h2>
            <ul className="mt-6 grid gap-3">
              {service.livrables.map((livrable) => (
                <li key={livrable} className="flex items-start gap-2.5 font-semibold">
                  <Check
                    className="mt-1 h-4 w-4 flex-none text-accent"
                    strokeWidth={2.6}
                    aria-hidden="true"
                  />
                  {livrable}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-[20px] border border-border bg-white p-8 lg:p-10">
            <h2 className="text-vt-h3 font-extrabold text-brand">Outils utilisés</h2>
            <ul className="mt-6 flex flex-wrap gap-2">
              {service.outils.map((outil) => (
                <li key={outil} className="tag">
                  {outil}
                </li>
              ))}
            </ul>
            <p className="mt-6 leading-relaxed text-t2">
              Des outils choisis pour rester maintenables par vos équipes après
              notre intervention.
            </p>
          </div>
        </div>
      </section>

      {formationsLiees.length > 0 && (
        <section className="section">
          <div className="shell">
            <h2 className="h2">Formations associées</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {formationsLiees.map((formation) => (
                <CarteFormation key={formation.slug} formation={formation} detaillee={false} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section section-alt">
        <div className="shell">
          <h2 className="h2">Nos autres services</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {autresServices.map((autre) => (
              <Link key={autre.slug} to={`/services/${autre.slug}`} className="card">
                <h3 className="text-vt-h3 font-bold text-brand">{autre.title}</h3>
                <p className="mt-2 flex-1 leading-relaxed text-t2">{autre.description}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-vt-controle font-bold text-brand">
                  En savoir plus
                  <ArrowRight className="h-[1.0625rem] w-[1.0625rem]" strokeWidth={2.4} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CallToAction />
    </>
  );
};
